import { useState, useEffect, useRef, useCallback } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import wmtLogoFull from "@/assets/wmt-logo-full.png";
import ContactsPanel from "@/components/ContactsPanel";
import { primaryNavigationItems } from "@/config/navigation";

const Navbar = () => {
  const { pathname, hash } = useLocation();
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  const isHome = normalizedPathname === "/";
  const hasGradientHero = isHome || normalizedPathname === "/education" || normalizedPathname === "/education/personal-ai";
  const faqHrefByPath: Record<string, string> = {
    "/": "/#faq",
    "/education": "/education#education-faq",
    "/education/personal-ai": "/education/personal-ai#personal-ai-faq",
    "/team/igor_nikitin": "/team/igor_nikitin#igor-faq",
    "/andrey_meinhardt": "/andrey_meinhardt#andrey-faq",
    "/team/yulia_boltutskaya": "/team/yulia_boltutskaya#yulia-faq",
  };
  const navSections = [
    ...primaryNavigationItems,
    { label: "FAQ", href: normalizedPathname.startsWith("/education/courses/") || normalizedPathname.startsWith("/courses/") ? `${normalizedPathname}#course-faq` : faqHrefByPath[normalizedPathname] ?? "/#faq" },
  ];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<number | null>(null);
  const [mobileSubmenu, setMobileSubmenu] = useState<string | null>(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    if (!hash) return;

    let attempts = 0;
    let timeoutId: number | undefined;
    const scrollToHash = () => {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
      if (attempts++ < 60) timeoutId = window.setTimeout(scrollToHash, 50);
    };

    scrollToHash();
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [hash, normalizedPathname]);

  useEffect(() => {
    if (activeMenu !== null) {
      const close = () => setActiveMenu(null);
      window.addEventListener("scroll", close, { passive: true });
      return () => window.removeEventListener("scroll", close);
    }
  }, [activeMenu]);

  const handleMouseEnter = useCallback((idx: number) => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
    hoverTimeout.current = setTimeout(() => setActiveMenu(idx), 120);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    leaveTimeout.current = setTimeout(() => {
      setActiveMenu(null);
      setActiveSubmenu(null);
    }, 150);
  }, []);

  const handleDropdownEnter = useCallback(() => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
  }, []);

  const scrollTo = (href: string) => {
    setActiveMenu(null);
    setActiveSubmenu(null);
    setMobileOpen(false);
    setMobileSubmenu(null);
    // Внешние ссылки (напр. «Обучение по ИИ» → wmt-landing) открываем в новой вкладке
    if (/^https?:\/\//.test(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (href.startsWith("/")) {
      const destination = new URL(href, window.location.origin);
      if (window.location.pathname === destination.pathname && destination.hash) {
        const target = document.getElementById(destination.hash.slice(1));
        window.history.replaceState(null, "", `${destination.pathname}${destination.hash}`);
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        } else {
          let attempts = 0;
          const scrollWhenReady = () => {
            const delayedTarget = document.getElementById(destination.hash.slice(1));
            if (delayedTarget) {
              delayedTarget.scrollIntoView({ behavior: "smooth" });
            } else if (attempts++ < 60) {
              window.setTimeout(scrollWhenReady, 50);
            }
          };
          scrollWhenReady();
        }
      } else if (window.location.pathname !== destination.pathname) {
        window.location.href = `${destination.pathname}${destination.hash}`;
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    const id = href.replace("#", "");
    const currentTarget = document.getElementById(id);
    if (currentTarget) {
      currentTarget.scrollIntoView({ behavior: "smooth" });
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    window.dispatchEvent(new CustomEvent("wmt:open-details", { detail: { target: id } }));
  };

  /** Внешний ли адрес (пока таким остаётся обучение на своём домене). */
  const isExternal = (href: string) => /^https?:\/\//.test(href);

  /**
   * Настоящий href для пункта меню.
   *
   * Пункты раньше были <button onClick>, а кнопка для робота — не ссылка: вес по
   * ней не течёт и раздел выглядит сиротой. Теперь это <a href>, а onClick лишь
   * добавляет плавную прокрутку. Якорь пишем от корня («/#directions»), иначе с
   * внутренних страниц он вёл бы в никуда.
   */
  const linkHref = (href: string) => href.startsWith("#") ? `/${href}` : href;

  /** Плавная прокрутка вместо перезагрузки — но только для обычного левого клика. */
  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return; // «открыть в новой вкладке» — браузеру
    if (isExternal(href)) {
      setActiveMenu(null);
      setMobileOpen(false);
      return; // внешнюю ссылку откроет сам <a target="_blank">
    }
    e.preventDefault();
    scrollTo(href);
  };


  return (
    <>
      <nav
        className={`fixed-ui-layer fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-200 ${
          scrolled
            ? "bg-background border-foreground/[0.07] shadow-[0_2px_12px_-6px_rgba(0,0,0,0.12)]"
            : hasGradientHero
              ? "bg-transparent border-transparent"
              : "bg-background/95 border-transparent"
        }`}
      >
        <div className="container mx-auto relative flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-3">
          {/* Logo */}
          <motion.a
            href={isHome ? "#" : "/"}
            className={`flex-shrink-0 transition-all duration-200 ${
              mobileOpen ? "opacity-0 blur-sm pointer-events-none lg:opacity-100 lg:blur-0 lg:pointer-events-auto" : ""
            }`}
            onClick={(e) => { if (isHome) { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            aria-hidden={mobileOpen ? true : undefined}
          >
            <img src={wmtLogoFull} alt="WMT AI" className="h-6 w-auto" />
          </motion.a>
          </div>

          {/* Desktop Nav — centered pill */}
          <div className="hidden lg:flex items-center absolute left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-0.5 bg-white rounded-full px-2 py-1.5 border border-black/[0.05] shadow-[0_6px_20px_-8px_rgba(0,0,0,0.22)]">
              {navSections.map((section, idx) => {
                const hasSubmenu = section.items && section.items.length > 0;

                if (!hasSubmenu) {
                  return (
                    <a
                      key={idx}
                      href={linkHref(section.href!)}
                      {...(isExternal(section.href!)
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="relative px-4 py-1.5 text-[14px] font-body font-medium tracking-wide text-[#303030] hover:text-accent transition-colors duration-200 rounded-full whitespace-nowrap"
                      onClick={(e) => handleNavClick(e, section.href!)}
                    >
                      <span className="optical-nudge">{section.label}</span>
                    </a>
                  );
                }

                return (
                  <div
                    key={idx}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(idx)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <a
                      href={linkHref(section.href!)}
                      onClick={(e) => handleNavClick(e, section.href!)}
                      className={`relative flex items-center gap-1 px-4 py-1.5 text-[14px] font-body font-medium tracking-wide rounded-full whitespace-nowrap transition-colors duration-200 ${
                        activeMenu === idx
                          ? "text-accent"
                          : "text-[#303030] hover:text-accent"
                      }`}
                    >
                      <span className="optical-nudge">{section.label}</span>
                      <ChevronDown
                        size={12}
                        className={`transition-transform duration-200 ${activeMenu === idx ? "rotate-180" : ""}`}
                      />
                    </a>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {activeMenu === idx && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.15, ease: "easeOut" }}
                          className="absolute left-0 top-full mt-3 min-w-[220px] rounded-2xl border border-black/[0.05] bg-white py-1.5 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)]"
                          onMouseEnter={handleDropdownEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          {section.items!.map((item, ii) => {
                            const submenuKey = `${idx}-${ii}`;
                            const hasNestedItems = Boolean(item.items?.length);

                            return (
                              <div
                                key={submenuKey}
                                className="relative"
                                onMouseEnter={() => setActiveSubmenu(hasNestedItems ? submenuKey : null)}
                              >
                                <a
                                  href={linkHref(item.href)}
                                  {...(isExternal(item.href)
                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                    : {})}
                                  onClick={(e) => handleNavClick(e, item.href)}
                                  className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm text-[#303030]/75 transition-colors duration-150 hover:bg-accent/[0.05] hover:text-accent"
                                >
                                  {item.label}
                                  {hasNestedItems && <ChevronRight size={14} />}
                                </a>

                                <AnimatePresence>
                                  {hasNestedItems && activeSubmenu === submenuKey && (
                                    <motion.div
                                      initial={{ opacity: 0, x: -4 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      exit={{ opacity: 0, x: -4 }}
                                      transition={{ duration: 0.15, ease: "easeOut" }}
                                      className="absolute left-[calc(100%-4px)] top-0 min-w-[210px] rounded-2xl border border-black/[0.05] bg-white py-1.5 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)]"
                                    >
                                      {item.items!.map((nestedItem) => (
                                        <a
                                          key={nestedItem.href}
                                          href={linkHref(nestedItem.href)}
                                          onClick={(e) => handleNavClick(e, nestedItem.href)}
                                          className="block px-4 py-2.5 text-sm text-[#303030]/75 transition-colors duration-150 hover:bg-accent/[0.05] hover:text-accent"
                                        >
                                          {nestedItem.label}
                                        </a>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              <button
                className={`relative px-4 py-1.5 text-[14px] font-body font-medium tracking-wide rounded-full whitespace-nowrap transition-colors duration-200 ${
                  contactsOpen ? "text-accent" : "text-[#303030] hover:text-accent"
                }`}
                onClick={() => setContactsOpen((v) => !v)}
              >
                <span className="optical-nudge">Контакты</span>
              </button>
            </div>
          </div>

          {/* CTA button — desktop */}
          <button
            onClick={() => scrollTo("#contact")}
            className="hidden lg:inline-flex items-center px-5 py-2 text-[14px] font-body font-medium tracking-wide rounded-full bg-[#303030] text-white hover:bg-[#404040] transition-all duration-200 whitespace-nowrap"
          >
            <span className="optical-nudge">Оставить заявку</span>
          </button>

        </div>
      </nav>

      <button
        type="button"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? "Закрыть меню" : "Открыть меню"}
        aria-expanded={mobileOpen}
        className="fixed bottom-5 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#303030] text-white shadow-[0_12px_30px_rgba(0,0,0,0.28)] lg:hidden"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* ─── Mobile Slide-in Menu ─── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm z-50 bg-background border-l border-border/50 overflow-y-auto lg:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-border/30">
                <img src={wmtLogoFull} alt="WMT AI" className="h-7 w-auto" />
                <button onClick={() => setMobileOpen(false)} aria-label="Закрыть меню" className="p-2 text-foreground">
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-1">
                {navSections.map((section, idx) => {
                  const hasSubmenu = section.items && section.items.length > 0;

                  if (!hasSubmenu) {
                    return (
                      <div key={idx} className="border-b border-border/20">
                        <a
                          href={linkHref(section.href!)}
                          {...(isExternal(section.href!)
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                          onClick={(e) => handleNavClick(e, section.href!)}
                          className="flex items-center justify-between w-full py-3 text-sm font-body font-medium text-foreground"
                        >
                          {section.label}
                        </a>
                      </div>
                    );
                  }

                  return (
                    <div key={idx} className="border-b border-border/20">
                      <div className="flex items-center">
                        <a
                          href={linkHref(section.href!)}
                          onClick={(e) => handleNavClick(e, section.href!)}
                          className="flex-1 py-3 text-sm font-body font-medium text-foreground"
                        >
                          {section.label}
                        </a>
                        <button
                          type="button"
                          onClick={() => setMobileExpanded(mobileExpanded === idx ? null : idx)}
                          className="flex h-11 w-11 items-center justify-center text-muted-foreground"
                          aria-label={`${mobileExpanded === idx ? "Скрыть" : "Показать"} разделы меню «${section.label}»`}
                          aria-expanded={mobileExpanded === idx}
                        >
                          <ChevronRight
                            size={16}
                            className={`transition-transform duration-200 ${
                              mobileExpanded === idx ? "rotate-90" : ""
                            }`}
                          />
                        </button>
                      </div>
                      <AnimatePresence>
                        {mobileExpanded === idx && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-1 pb-3 pl-2">
                              {section.items!.map((item, ii) => {
                                const submenuKey = `${idx}-${ii}`;
                                const hasNestedItems = Boolean(item.items?.length);

                                return (
                                  <div key={submenuKey}>
                                    <div className="flex items-center">
                                      <a
                                        href={linkHref(item.href)}
                                        {...(isExternal(item.href)
                                          ? { target: "_blank", rel: "noopener noreferrer" }
                                          : {})}
                                        onClick={(e) => handleNavClick(e, item.href)}
                                        className="flex-1 py-2 pl-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                                      >
                                        {item.label}
                                      </a>
                                      {hasNestedItems && (
                                        <button
                                          type="button"
                                          onClick={() => setMobileSubmenu(mobileSubmenu === submenuKey ? null : submenuKey)}
                                          className="flex h-10 w-10 items-center justify-center text-muted-foreground"
                                          aria-label={`${mobileSubmenu === submenuKey ? "Скрыть" : "Показать"} подразделы меню «${item.label}»`}
                                          aria-expanded={mobileSubmenu === submenuKey}
                                        >
                                          <ChevronRight size={15} className={`transition-transform ${mobileSubmenu === submenuKey ? "rotate-90" : ""}`} />
                                        </button>
                                      )}
                                    </div>
                                    {hasNestedItems && mobileSubmenu === submenuKey && (
                                      <div className="space-y-1 pb-2 pl-5">
                                        {item.items!.map((nestedItem) => (
                                          <a
                                            key={nestedItem.href}
                                            href={linkHref(nestedItem.href)}
                                            onClick={(e) => handleNavClick(e, nestedItem.href)}
                                            className="block py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                                          >
                                            {nestedItem.label}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}

                <div className="border-b border-border/20">
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      setContactsOpen(true);
                    }}
                    className="flex items-center justify-between w-full py-3 text-sm font-body font-medium text-foreground"
                  >
                    Контакты
                  </button>
                </div>

                {/* Mobile CTA */}
                <div className="pt-4">
                <button
                  onClick={() => scrollTo("#contact")}
                  className="w-full py-3 text-sm font-body font-medium tracking-wide rounded-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-200"
                >
                  Оставить заявку
                </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ContactsPanel open={contactsOpen} onClose={() => setContactsOpen(false)} />
    </>
  );
};

export default Navbar;
